/**
 * 端末時計。既存プロトコル最大の欠陥を埋めるための仕組み。
 *
 * 中継ストリームが運ぶのは「送信元アプリ起動からの経過ミリ秒」であって
 * 絶対時刻ではない。しかも原典の受信側はこの値を読み捨てている。
 * このままでは複数機の時刻を揃えられず、4次元の風場を作れない（docs/01 §8 課題2）。
 *
 * 一方 NMEA センテンス自身は UTC を持っている。
 *   $GPRMC … 時刻＋日付 → 絶対時刻が完全に決まる
 *   $GPGGA … 時刻のみ   → 日付は別途必要
 *   $GPZDA … 時刻＋日付 → $GPRMC と同等
 *
 * そこで端末ごとに「経過ms → UTC」のオフセットを学習し、
 * UTC を持たないセンテンス（$_MARK など）にも時刻を与えられるようにする。
 */

import { ELAPSED_WRAP } from './line.ts';

/** オフセット推定に使う標本数。中央値を取るので外れ値に強い */
const SAMPLE_WINDOW = 9;

/** 未来側にどこまで許容するか。時計の多少のずれを吸収する程度の余裕 */
const MAX_FUTURE_MS = 24 * 60 * 60 * 1000;
/** 過去側にどこまで許容するか。古いIGCの読み込みも通す必要があるので広めに取る */
const MAX_PAST_MS = 5 * 365 * 24 * 60 * 60 * 1000;

/**
 * ある絶対時刻 [UTC ms] が「本物」としてもっともらしいか。
 *
 * ★ なぜ要るか（2026-08-21 に実際に確認した不具合）
 *   中継ストリームは外部の任意の端末が送ってくる生データで、RTC未同期や
 *   機材の不具合などにより明らかにおかしい日付（例: 西暦2070年）を持つ
 *   `$GPRMC`/`$GPZDA` が実際に流れてくることがある。これを無条件に
 *   信じると、`TerminalClock` のオフセット学習と `decode.ts` の
 *   `dayStartMs` の両方が汚染され、**以後その端末の全測位が誤った日付の
 *   まま固定される**（サーバー再起動か `/api/reset` までは直らない）。
 *   結果、その端末の航跡は実時刻とかけ離れた場所に位置するため、
 *   「直近N時間」で絞り込む画面（ウィンドプロファイラなど）から
 *   一切消えてしまう——という、原因の分かりにくい壊れ方をする。
 */
export function isPlausibleAbsoluteMs(utcMs: number, referenceMs: number): boolean {
  return utcMs <= referenceMs + MAX_FUTURE_MS && utcMs >= referenceMs - MAX_PAST_MS;
}

/**
 * ライブ測位の時刻が「今より先」に許容できる猶予[ms]。
 *
 * ★ なぜ要るか（2026-09-13、TV側の実機で実際に確認した不具合）
 *   TVのバージョン違いにより、実際より20〜40分ほど先の時刻を持つ測位が
 *   紛れ込み、航跡がその分だけ「飛ぶ」（実際は同じ位置のデータが時刻違いで
 *   複数出ていた）不具合が現地で発生した。GV4D自体はこの不具合を経験して
 *   いないが、同じ弱点は仕組みとして持っている: 「直前に採用した点より
 *   新しい時刻の点だけを次の基準にする」方式（`dedupe.ts`のisNewerFix、
 *   `RelayState`/`BridgeConnection`の`latest`更新）は、一度でも未来方向へ
 *   ズレた時刻の点を基準として採用してしまうと、以後に届く「本当に正しい」
 *   現在時刻の測位がすべて「基準より古い」と判定されて捨てられ続け、
 *   実時刻がその誤った基準に追いつくまで航跡が更新されなくなる
 *   （まさに今回TVで起きたのと同じ症状になりうる）。
 *
 *   `isPlausibleAbsoluteMs`（未来24時間まで許容）は、日付学習・古いIGCの
 *   取り込みなど別の用途のために意図的に広く取ってあるため、この
 *   数十分オーダーのズレは素通ししてしまう。ライブ測位の「今」判定には
 *   もっと狭い、専用の猶予を使う。
 */
export const LIVE_FIX_FUTURE_TOLERANCE_MS = 5 * 60_000;

/**
 * ライブ測位の時刻`fixT`が、`nowMs`から見て未来方向にもっともらしいか。
 *
 * 実世界のGPS測位が実際の現在時刻より先の時刻を持つことは無い
 * （多少の端末の時計のズレは許容する必要があるため、既定5分の猶予を持たせる）。
 * これを超えて未来の測位は、`LIVE_FIX_FUTURE_TOLERANCE_MS`のコメントに
 * 書いた「以後の正しい測位を巻き添えで捨て続ける」事故を防ぐため、
 * 「直前より新しい」判定・`latest`の更新のどちらにも採用してはならない。
 *
 * 過去方向には制限を設けない（電波状況で遅れて届くのは正常な挙動であり、
 * 順序の入れ替わりは呼び出し側（`isNewerFix`等）が別途扱う）。
 */
export function isPlausibleLiveFixTime(
  fixT: number,
  nowMs: number = Date.now(),
  toleranceMs: number = LIVE_FIX_FUTURE_TOLERANCE_MS,
): boolean {
  return fixT <= nowMs + toleranceMs;
}

interface TerminalState {
  /** 経過msのラップ回数（10^8 ミリ秒 ≒ 27.7時間ごと） */
  wraps: number;
  lastElapsed: number;
  /** utcMs - 連続化した経過ms の標本 */
  offsets: number[];
}

export interface ResolvedTime {
  /** UTC epoch ミリ秒 */
  t: number;
  /**
   * 'measured' … センテンス自身の UTC から直接決まった
   * 'derived'  … 学習したオフセットから算出した
   * 'arrival'  … 手掛かりが無く受信時刻で代用した（最も粗い）
   */
  quality: 'measured' | 'derived' | 'arrival';
}

export class TerminalClock {
  #states = new Map<string, TerminalState>();

  #state(terminalId: string): TerminalState {
    let s = this.#states.get(terminalId);
    if (!s) {
      s = { wraps: 0, lastElapsed: -1, offsets: [] };
      this.#states.set(terminalId, s);
    }
    return s;
  }

  /**
   * 経過msを連続値に直す。
   * 10^8 で折り返すため、大きく後戻りしたらラップとみなす。
   */
  #continuous(s: TerminalState, elapsedMs: number): number {
    if (s.lastElapsed >= 0 && elapsedMs < s.lastElapsed - ELAPSED_WRAP / 2) s.wraps++;
    s.lastElapsed = elapsedMs;
    return elapsedMs + s.wraps * ELAPSED_WRAP;
  }

  /**
   * 絶対時刻が判明したセンテンスを与えてオフセットを学習させる。
   *
   * ★ `utcMs` が明らかにおかしい（`isPlausibleAbsoluteMs` 参照）場合は
   *   黙って無視する。オフセットは中央値を取るので1〜2個の外れ値には
   *   本来強いが、そもそも学習させないほうが安全（このチェックが無いと
   *   9個の標本のうち1個が全て埋まるまでの間、誤った日付が
   *   採用され続ける）。
   *
   * @param utcMs $GPRMC / $GPZDA から得た UTC epoch ミリ秒
   * @param referenceMs もっともらしさの基準にする時刻。既定は現在時刻
   */
  observeAbsolute(
    terminalId: string,
    elapsedMs: number,
    utcMs: number,
    referenceMs: number = Date.now(),
  ): void {
    if (!isPlausibleAbsoluteMs(utcMs, referenceMs)) return;
    const s = this.#state(terminalId);
    const cont = this.#continuous(s, elapsedMs);
    s.offsets.push(utcMs - cont);
    if (s.offsets.length > SAMPLE_WINDOW) s.offsets.shift();
  }

  /**
   * 時刻だけ分かるセンテンス（$GPGGA など）から学習する。
   * 日付は `referenceMs`（既知の UTC。受信時刻でもよい）の日付を使い、
   * 日またぎは前後1日のうち最も近いものを選ぶ。
   */
  observeTimeOfDay(
    terminalId: string,
    elapsedMs: number,
    utcSeconds: number,
    referenceMs: number,
  ): void {
    const dayStart = Math.floor(referenceMs / 86400000) * 86400000;
    const candidates = [
      dayStart + utcSeconds * 1000 - 86400000,
      dayStart + utcSeconds * 1000,
      dayStart + utcSeconds * 1000 + 86400000,
    ];
    let best = candidates[0];
    for (const c of candidates) {
      if (Math.abs(c - referenceMs) < Math.abs(best - referenceMs)) best = c;
    }
    this.observeAbsolute(terminalId, elapsedMs, best, referenceMs);
  }

  /** 学習済みのオフセット [ms]。まだ学習していなければ null */
  offsetOf(terminalId: string): number | null {
    const s = this.#states.get(terminalId);
    if (!s || s.offsets.length === 0) return null;
    const sorted = [...s.offsets].sort((a, b) => a - b);
    return sorted[Math.floor(sorted.length / 2)];
  }

  /**
   * 経過msを UTC に解決する。
   *
   * @param receivedAtMs 受信時刻。オフセット未学習のときの代用に使う
   */
  resolve(terminalId: string, elapsedMs: number, receivedAtMs?: number): ResolvedTime | null {
    const s = this.#state(terminalId);
    const cont = this.#continuous(s, elapsedMs);
    const offset = this.offsetOf(terminalId);
    if (offset !== null) return { t: cont + offset, quality: 'derived' };
    if (receivedAtMs !== undefined) return { t: receivedAtMs, quality: 'arrival' };
    return null;
  }

  /** 追跡中の端末ID一覧 */
  terminals(): string[] {
    return [...this.#states.keys()];
  }

  reset(terminalId?: string): void {
    if (terminalId) this.#states.delete(terminalId);
    else this.#states.clear();
  }
}
